import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Plus, Trash2, AlertTriangle } from "lucide-react";
import withholdingTaxAPI from "../../api/withholdingTaxAPI";
import ChartOfAccountsSearch from "../ChartOfAccountsSearch";
import Button from "../Button";
import MoneyInput from "../MoneyInput";
import RestoreDraftModal from "../RestoreDraftModal";
import SaveOutcomePanel from "../SaveOutcomePanel";
import DraftStatusIndicator from "../DraftStatusIndicator";
import { EmployeePicker, employeeName } from "./WhtPickers";
import { DETAIL_FIELDS, fieldEnabledFor } from "./compFields";
import notify from "../../lib/notify";
import useGuardedMutation from "../../hooks/useGuardedMutation";
import useEncodingDraft from "../../hooks/useEncodingDraft";
import { DRAFT_SCREENS } from "../../utils/encodingDraft";
import { setPendingIdempotencyKey } from "../../utils/idempotencyKey";
import { classifyRows, newClientRowId, rowsAfterSave, withClientRowIds } from "../../utils/encodingGrid";
import { formatCentavos, pesosToCentavos } from "../../utils/whtMoney";

// Compensation (COMP) encoding grid — one row per employee per month (D11),
// feeding 1601-C. Same look and save contract as EwtRecordsGrid: 5 blank rows,
// draft autosave, incomplete rows stay highlighted, one all-or-nothing batch.
// Tax withheld (item 25) is entered, not computed.

export const MIN_ROWS = 5;
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const AMOUNT_KEYS = ["totalCompCentavos", "taxWithheldCentavos", ...DETAIL_FIELDS.map((f) => f.key)];

export const buildEmptyRow = (month) => ({
  id: newClientRowId(),
  month: month || "",
  description: "",
  coaAccount: null,
  coaName: "",
  employee: null, // { _id, lastName, firstName, middleName, tin, isMwe }
  ...Object.fromEntries(AMOUNT_KEYS.map((k) => [k, 0])), // pesos (MoneyInput)
  otherNonTaxableSpecify: "",
});
const buildEmptyRows = (month) => Array.from({ length: MIN_ROWS }, () => buildEmptyRow(month));

export const isRowEmpty = (row) =>
  !row?.employee && !(row?.description || "").trim() && AMOUNT_KEYS.every((k) => !Number(row?.[k]));

const isRowIncomplete = (row) => !isRowEmpty(row) && (!row.month || !row.employee || !(pesosToCentavos(row.totalCompCentavos) > 0));

// Which cell shows a server field error.
const FIELD_CELL = {
  month: "month", employee: "employee", description: "description", coaAccount: "description",
  totalCompCentavos: "total", taxWithheldCentavos: "withheld",
};

const toPayloadRow = (row) => ({
  clientRowId: row.id,
  month: Number(row.month),
  employee: row.employee?._id,
  description: (row.description || "").trim(),
  coaAccount: row.coaAccount || null,
  ...Object.fromEntries(AMOUNT_KEYS.map((k) => [k, pesosToCentavos(row[k]) || 0])),
  otherNonTaxableSpecify: (row.otherNonTaxableSpecify || "").trim(),
});

const FieldError = ({ messages }) =>
  messages?.length ? <p role="alert" className="text-xs text-danger-500 mt-0.5">{messages.join("; ")}</p> : null;

const inputClass = "w-full border rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500";

const CompRecordsGrid = ({ client, year, defaultMonth, onSaved }) => {
  const [rows, setRows] = useState(() => buildEmptyRows(defaultMonth));
  const [expanded, setExpanded] = useState(() => new Set());
  const [rowErrors, setRowErrors] = useState({});
  const [outcomes, setOutcomes] = useState([]);
  const [savedWarnings, setSavedWarnings] = useState([]);
  const clientId = client?._id;

  const draft = useEncodingDraft({ screen: DRAFT_SCREENS.WHT_COMP, clientId, fiscalYear: year, rows, isRowEmpty });

  const updateRow = useCallback((id, patch) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...(typeof patch === "function" ? patch(r) : patch) } : r)));
  }, []);

  useEffect(() => {
    if (!defaultMonth) return;
    setRows((prev) => prev.map((r) => (r.month ? r : { ...r, month: defaultMonth })));
  }, [defaultMonth]);

  const toggleExpanded = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  // Picking an employee clears amounts the employee cannot carry (15/16 for a
  // non-MWE, 23 for a MWE) — the server would reject them anyway.
  const handleEmployee = (id, employee) => updateRow(id, () => {
    const patch = { employee };
    if (employee) for (const f of DETAIL_FIELDS) if (!fieldEnabledFor(f, employee)) patch[f.key] = 0;
    return patch;
  });

  const filledCount = useMemo(() => rows.filter((r) => !isRowEmpty(r)).length, [rows]);
  const incompleteCount = useMemo(() => rows.filter(isRowIncomplete).length, [rows]);
  const totals = useMemo(() => rows.reduce((acc, r) => (isRowEmpty(r) ? acc : {
    total: acc.total + (pesosToCentavos(r.totalCompCentavos) || 0),
    withheld: acc.withheld + (pesosToCentavos(r.taxWithheldCentavos) || 0),
  }), { total: 0, withheld: 0 }), [rows]);

  const handleSave = async () => {
    if (!clientId || !year) {
      notify.error("Couldn't save", "Select a client and a year first.");
      return;
    }
    const { toSave, incomplete } = classifyRows(rows, isRowIncomplete, isRowEmpty);
    setRowErrors({});
    setSavedWarnings([]);
    if (toSave.length === 0) {
      notify.error("Couldn't save", incomplete.length
        ? `${incomplete.length} row${incomplete.length !== 1 ? "s are" : " is"} incomplete — each needs a month, an employee and total compensation. Nothing was sent.`
        : "Add at least one row before saving.");
      return;
    }

    const payloadRows = toSave.map(toPayloadRow);
    const groupId = `wht-comp-${year}`;
    const label = `Compensation records · ${year}`;
    const { key } = draft.keyForGroup(groupId, { year, rows: payloadRows });
    setPendingIdempotencyKey(key);

    let response;
    try {
      response = await withholdingTaxAPI.saveCompRecords(clientId, { year, rows: payloadRows });
    } catch (error) {
      const hasResponse = Boolean(error?.status);
      if (error?.lineErrors?.length) {
        const byRow = {};
        for (const e of error.lineErrors) {
          const rowId = toSave[(e.line ?? 0) - 1]?.id;
          if (!rowId) continue;
          const cells = (byRow[rowId] ||= {});
          for (const [field, message] of Object.entries(e.fields || {})) {
            const cell = FIELD_CELL[field] || "detail";
            (cells[cell] ||= []).push(message);
          }
        }
        setRowErrors(byRow);
        // Detail-field errors are inside ▸ — open those rows.
        setExpanded((prev) => new Set([...prev, ...Object.entries(byRow).filter(([, c]) => c.detail).map(([id]) => id)]));
      }
      setOutcomes([{
        id: groupId, label, status: hasResponse ? "failed" : "unknown", sentCount: payloadRows.length,
        message: hasResponse
          ? `${error.message || "The server rejected this batch"} — nothing was saved. Fix the highlighted rows and save again.`
          : "No response from the server — these rows may or may not have been saved. Check the records below before saving again.",
      }]);
      notify.error(hasResponse ? "Couldn't save" : "Save unconfirmed", "Your rows are still here.");
      return;
    }

    const saved = response?.data || [];
    const confirmedIds = saved.map((d) => d.clientRowId).filter(Boolean);
    setRows((prev) => rowsAfterSave(prev, confirmedIds, MIN_ROWS, () => buildEmptyRow(defaultMonth)));
    draft.clearConfirmedRows(confirmedIds);
    draft.releaseKey(groupId);

    const perRow = new Map();
    for (const w of response?.warnings || []) {
      const sent = toSave[(w.line ?? 0) - 1];
      if (!sent) continue;
      if (!perRow.has(sent.id)) {
        perRow.set(sent.id, { id: sent.id, label: `${employeeName(sent.employee)} · ${MONTHS[Number(sent.month) - 1]}`, items: [] });
      }
      perRow.get(sent.id).items.push({ code: w.code, message: w.message });
    }
    setSavedWarnings([...perRow.values()]);
    setOutcomes([{ id: groupId, label, status: "saved", sentCount: payloadRows.length, savedCount: saved.length }]);
    notify.success("Saved", `${saved.length} compensation record${saved.length !== 1 ? "s" : ""} for ${year}.`);
    onSaved?.(saved);
  };

  const { mutate: runSave, isPending: saving } = useGuardedMutation(handleSave);
  const runSaveSafely = () => {
    runSave()?.catch((error) => {
      console.error("Unexpected error while saving compensation records:", error);
      notify.error("Couldn't save", "Something went wrong before the rows could be sent. Your rows are still here.");
    });
  };

  return (
    <div>
      <RestoreDraftModal
        draft={draft.pendingDraft}
        onRestore={() => { const r = draft.restoreDraft(); if (r?.length) setRows(withClientRowIds(r)); }}
        onDiscard={draft.discardDraft}
        screenLabel="compensation records"
      />

      {outcomes.length > 0 && <div className="mb-4"><SaveOutcomePanel outcomes={outcomes} onDismiss={() => setOutcomes([])} /></div>}

      {savedWarnings.length > 0 && (
        <div role="status" aria-label="Saved with warnings" className="mb-4 border border-warning-200 bg-warning-50 rounded-lg p-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-warning-700 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Saved with warnings — please review</p>
            <Button variant="ghost" size="sm" onClick={() => setSavedWarnings([])}>Dismiss</Button>
          </div>
          <ul className="mt-2 space-y-1">
            {savedWarnings.map((w) => (
              <li key={w.id} data-testid="saved-warning" className="text-sm text-warning-700">
                <span className="font-medium">{w.label}:</span> {w.items.map((i) => i.message).join(" · ")}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="overflow-x-auto border border-ink-line rounded-lg">
        <table className="min-w-full divide-y divide-ink-line">
          <thead className="bg-ink-canvas">
            <tr>
              <th className="w-10 px-2 py-3"><span className="sr-only">Details</span></th>
              <th className="px-3 py-3 text-left text-xs font-medium text-ink-body uppercase tracking-wider w-[9%]">Month</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-ink-body uppercase tracking-wider w-[25%]">Description / COA</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-ink-body uppercase tracking-wider w-[25%]">Employee</th>
              <th className="px-3 py-3 text-right text-xs font-medium text-ink-body uppercase tracking-wider w-[16%]">Total comp (14)</th>
              <th className="px-3 py-3 text-right text-xs font-medium text-ink-body uppercase tracking-wider w-[16%]">Tax withheld (25)</th>
              <th className="px-3 py-3 text-center text-xs font-medium text-ink-body uppercase tracking-wider w-[5%]">Action</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-ink-line">
            {rows.flatMap((row, index) => {
              const errors = rowErrors[row.id] || {};
              const invalid = isRowIncomplete(row) || Object.keys(errors).length > 0;
              const isExpanded = expanded.has(row.id);
              const n = index + 1;

              const main = (
                <tr key={row.id} data-row-id={row.id} data-invalid={invalid ? "true" : undefined} className="hover:bg-ink-canvas/60">
                  <td className={`px-2 py-2 text-center align-top ${invalid ? "border-l-2 border-danger-400" : ""}`}>
                    <button type="button" onClick={() => toggleExpanded(row.id)} className="text-ink-body hover:text-ink"
                      aria-label={isExpanded ? `Collapse row ${n}` : `Expand row ${n}`} aria-expanded={isExpanded}>
                      {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </button>
                  </td>
                  <td className="px-3 py-2 align-top">
                    <select aria-label={`Month row ${n}`} value={row.month || ""} onChange={(e) => updateRow(row.id, { month: Number(e.target.value) || "" })}
                      className={`${inputClass} ${errors.month ? "border-danger-500" : "border-ink-line"}`}>
                      <option value="" disabled>Select</option>
                      {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                    </select>
                    <FieldError messages={errors.month} />
                  </td>
                  <td className="px-3 py-2 align-top">
                    <ChartOfAccountsSearch
                      value={row.coaName || row.description}
                      onChange={(value) => updateRow(row.id, (r) => ({ description: value, ...(value && !r.coaName ? { coaAccount: null } : {}) }))}
                      onSelect={(a) => updateRow(row.id, a
                        ? { coaAccount: a._id, coaName: `${a.accountNumber} - ${a.accountName}`, description: a.accountName }
                        : { coaAccount: null, coaName: "", description: "" })}
                      placeholder="Search or enter description"
                      showLinkedBadge={false}
                    />
                    <FieldError messages={errors.description} />
                  </td>
                  <td className="px-3 py-2 align-top">
                    <EmployeePicker clientId={clientId} value={row.employee} onChange={(e) => handleEmployee(row.id, e)}
                      inputId={`employee-${row.id}`} ariaLabel={`Employee row ${n}`} invalid={Boolean(errors.employee)} />
                    {row.employee?.isMwe && <span className="text-xs text-primary-700">Minimum wage earner</span>}
                    <FieldError messages={errors.employee} />
                  </td>
                  <td className="px-3 py-2 align-top" data-testid={`total-${n}`}>
                    <MoneyInput id={`total-${row.id}`} value={row.totalCompCentavos} onValueChange={(v) => updateRow(row.id, { totalCompCentavos: v })}
                      className={`w-full ${errors.total ? "border-danger-500" : ""}`} />
                    <FieldError messages={errors.total} />
                  </td>
                  <td className="px-3 py-2 align-top" data-testid={`withheld-${n}`}>
                    <MoneyInput id={`withheld-${row.id}`} value={row.taxWithheldCentavos} onValueChange={(v) => updateRow(row.id, { taxWithheldCentavos: v })}
                      className={`w-full ${errors.withheld ? "border-danger-500" : ""}`} />
                    <FieldError messages={errors.withheld} />
                  </td>
                  <td className="px-2 py-2 text-center align-top">
                    <button type="button" onClick={() => setRows((prev) => (prev.length <= MIN_ROWS ? prev : prev.filter((r) => r.id !== row.id)))}
                      disabled={rows.length <= MIN_ROWS} aria-label={`Remove row ${n}`} className="text-danger-500 hover:text-danger-700 disabled:text-ink-line">
                      <Trash2 className="w-4 h-4 mx-auto" />
                    </button>
                  </td>
                </tr>
              );
              if (!isExpanded) return [main];

              const detail = (
                <tr key={`${row.id}-detail`} data-detail-for={row.id}>
                  <td colSpan={7} className="bg-ink-canvas px-6 py-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
                      {DETAIL_FIELDS.map((f) => {
                        const enabled = fieldEnabledFor(f, row.employee);
                        return (
                          <div key={f.key} className="grid grid-cols-[2.5rem_1fr_9rem] items-center gap-2">
                            <span className="text-xs font-medium text-primary-700 bg-primary-50 rounded-md px-2 py-0.5 text-center">{f.item}</span>
                            <label htmlFor={`${f.key}-${row.id}`} className={`text-xs ${enabled ? "text-ink-body" : "text-ink-line"}`}>
                              {f.label}
                              {!enabled && <span className="block">{f.mwe === "only" ? "Minimum wage earners only" : "Not for minimum wage earners"}</span>}
                            </label>
                            <MoneyInput id={`${f.key}-${row.id}`} value={row[f.key]} onValueChange={(v) => updateRow(row.id, { [f.key]: v })} disabled={!enabled} className="w-full" />
                          </div>
                        );
                      })}
                      <label className="md:col-span-2 text-xs text-ink-body">Item 20 — specify
                        <input aria-label={`Other non-taxable compensation (specify) row ${n}`} value={row.otherNonTaxableSpecify}
                          onChange={(e) => updateRow(row.id, { otherNonTaxableSpecify: e.target.value })}
                          className={`${inputClass} border-ink-line bg-white mt-1`} />
                      </label>
                    </div>
                    <FieldError messages={errors.detail} />
                  </td>
                </tr>
              );
              return [main, detail];
            })}
          </tbody>
          <tfoot className="bg-ink-canvas border-t-2 border-ink-line">
            <tr>
              <td className="px-2 py-3" />
              <td colSpan={3} className="px-3 py-3 text-sm font-medium text-ink-body">
                Totals ({filledCount} filled, {rows.length - filledCount} empty)
                {incompleteCount > 0 && <span className="ml-2 text-danger-500">{incompleteCount} incomplete</span>}
              </td>
              <td className="px-3 py-3 text-sm font-medium text-ink text-right tabular-nums" data-testid="total-14">₱{formatCentavos(totals.total)}</td>
              <td className="px-3 py-3 text-sm font-medium text-ink text-right tabular-nums" data-testid="total-25">₱{formatCentavos(totals.withheld)}</td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="mt-4 flex justify-between items-center">
        <Button variant="outline" size="sm" icon={Plus} onClick={() => setRows((prev) => [...prev, ...buildEmptyRows(defaultMonth)])}>Add 5 more rows</Button>
        <div className="flex items-center gap-3">
          <Button variant="dangerOutline" onClick={() => { setRows(buildEmptyRows(defaultMonth)); setRowErrors({}); }}>Clear all</Button>
          <DraftStatusIndicator lastSavedAt={draft.lastSavedAt} isSaving={draft.isSaving} />
          <div className="text-right">
            <Button onClick={runSaveSafely} loading={saving} disabled={saving || !clientId || !year}>
              {saving ? "Saving…" : `Save ${filledCount} record${filledCount !== 1 ? "s" : ""} (${year || "—"})`}
            </Button>
            <p className="text-xs text-ink-body mt-1">Empty rows are skipped; incomplete rows stay in the grid</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompRecordsGrid;
